import { useEffect, useRef } from 'react';
import { PLANS } from '../constants/content';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackDownloadClick, trackPricingView } from '../services/analytics';

export default function Pricing() {
  const ref = useScrollAnimation();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          trackPricingView();
          hasTrackedRef.current = true;
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return (
    <section id="pricing" ref={ref} className="fade-in mx-auto w-full max-w-6xl px-4 py-16 md:px-8">
      <h2 className="font-[Nunito] text-3xl font-extrabold text-[#5B3FA6] md:text-4xl">Start free, upgrade when ready</h2>
      <p className="mt-3 text-lg text-[#6F655E]">No tricks. No bait-and-switch. The free tier is genuinely useful.</p>
      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {PLANS.map((plan, index) => (
          <article
            key={plan.name}
            className={`rounded-2xl bg-white p-6 shadow-sm ${plan.highlighted ? 'scale-[1.02] border-2 border-[#5B3FA6]' : 'border border-[#F0E7DD]'}`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            {plan.badge && (
              <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${plan.highlighted ? 'bg-[#5B3FA6] text-white' : 'bg-[#FFE6DE] text-[#C95D41]'}`}>
                {plan.badge}
              </span>
            )}
            <h3 className="mt-4 font-[Nunito] text-2xl font-extrabold text-[#4A3F66]">{plan.name}</h3>
            <p className="mt-2 font-[Nunito] text-4xl font-black text-[#5B3FA6]">{plan.price}</p>
            {plan.subtitle ? <p className="mt-1 text-sm text-[#7B716A]">{plan.subtitle}</p> : null}
            {plan.trial ? <p className="mt-2 text-sm font-semibold text-[#5B3FA6]">{plan.trial}</p> : null}
            <ul className="mt-5 space-y-2 text-[#5F5650]">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <span className="text-[#4A9F57]">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <a
              href={plan.buttonHref}
              onClick={() => trackDownloadClick(`pricing_${plan.name.toLowerCase().replace(/\s+/g, '_')}`)}
              className={`mt-6 inline-block w-full rounded-full px-5 py-3 text-center font-semibold ${
                plan.buttonVariant === 'solid'
                  ? 'bg-[#FF7B5B] text-white shadow-sm hover:shadow-md'
                  : 'border-2 border-[#5B3FA6] text-[#5B3FA6]'
              }`}
            >
              {plan.buttonLabel}
            </a>
          </article>
        ))}
      </div>
      <p className="mt-6 text-center text-sm text-[#7B716A]">🔒 Cancel anytime. No questions asked.</p>
    </section>
  );
}

